import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

export type ThemeMode = 'light' | 'dark'

const THEME_KEY = 'agencyos-theme'

interface ThemeContextType {
  theme: ThemeMode
  setTheme: (theme: ThemeMode) => void
  isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    // Check localStorage first
    const stored = localStorage.getItem(THEME_KEY) as ThemeMode | null
    if (stored === 'light' || stored === 'dark') {
      return stored
    }
    // Default to light theme (current theme)
    return 'light'
  })

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }, [theme])

  const setTheme = useCallback((newTheme: ThemeMode) => {
    localStorage.setItem(THEME_KEY, newTheme)
    setThemeState(newTheme)
  }, [])

  const value: ThemeContextType = {
    theme,
    setTheme,
    isDark: theme === 'dark',
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

